/**
 * @fileoverview B49: the decisions of a mark on the to-do list (a tick, a skip, a postpone, their Undos, a delete),
 * apart from the screen, so they are tested without one. The server answers a mark as soon as it is saved, with what
 * to say and no brief (POST /todo/marks); the brief is then read again on its own (GET /today/brief). Building the
 * brief took past the app's 30 s limit on a slow server, and a saved tick was shown as failed and put back.
 *
 * - A mark shows at once (tickOf: the job ticked or skipped, or its plants), and stays shown once saved, until a
 *   brief asked for after it arrives (shownAfterBrief). A slow or failed read of the brief says nothing and puts
 *   nothing back: the mark is saved, and the screen keeps showing it.
 * - A mark that got no answer at all (a timeout, a dropped connection) is judged by reading the brief again, as B43
 *   does for own to-dos: there (markLanded), it counts as saved; not there, or the read fails, it is the standard
 *   failure. A 4xx or 5xx is a failure as it always was.
 *
 * Pure: no React, no request of its own (the caller hands in send and refresh). Type imports only, so node's test
 * runner loads it as it is.
 *
 * Where: useTodoMarks.
 * API: none (the caller sends POST /todo/marks and reads GET /today/brief).
 *
 * @module today/lib/todoMarkFlow
 */

import type { TodoMark, TodoMarkAnswer, TodoSaid } from '@/shared/api/todo';
import type { TodayBrief, TodoJob } from '@/shared/api/todayBrief';

/** A mark shown before the brief says it: the job's done state, or its plants', and whether it is skipped. */
export interface PendingTick {
  /** The whole job, done or not (a skip shows it done and struck through, as the brief says a skipped job). */
  done?: boolean;
  /** Some of its plants and whether they are done. */
  plantIds?: number[];
  plantsDone?: boolean;
  /** Skipped (true) or the skip taken back (false). */
  skipped?: boolean;
  /** The line the server says under it once saved (a skip's "Skipped. Grow won't ask about this one again."). */
  line?: string;
}

/** One mark shown on a job: what it shows, and when it was saved (null while it is on its way). */
export interface ShownMark {
  tick: PendingTick;
  savedAt: number | null;
}

/** The marks shown, by job key. */
export type Shown = Record<string, ShownMark>;

/**
 * What a mark shows at once, before any answer: a tick or untick (the job, or some plants), a skip or its Undo.
 * Nothing for a mark that moves the job (a postpone, When you can, back, a day, a delete): the toast says those once
 * saved, and the brief read after them moves the job.
 *
 * @param mark - The mark.
 * @returns The tick to show, or null.
 */
export function tickOf(mark: TodoMark): PendingTick | null {
  if (mark.action === 'done' || mark.action === 'undone') {
    const done = mark.action === 'done';
    return mark.plantIds ? { plantIds: mark.plantIds, plantsDone: done } : { done };
  }
  if (mark.action === 'skip') return { done: true, skipped: true };
  if (mark.action === 'unskip') return { done: false, skipped: false };
  return null;
}

/**
 * A mark set off: shown at once (on its way), when it shows anything.
 *
 * @param shown - The marks shown.
 * @param key - The job's key.
 * @param tick - What it shows (tickOf), or null for nothing.
 * @returns The marks shown now.
 */
export function shownStarted(shown: Shown, key: string, tick: PendingTick | null): Shown {
  if (!tick) return shown;
  return { ...shown, [key]: { tick, savedAt: null } };
}

/**
 * A mark saved: kept shown, with when it was saved, until a brief asked for after it arrives; with the line the
 * server said for it (said.line: a skip's), so the card says it before the brief does.
 *
 * @param shown - The marks shown.
 * @param key - The job's key.
 * @param savedAt - When the answer came (ms).
 * @param line - The server's line for it, if it said one.
 * @returns The marks shown now.
 */
export function shownSaved(shown: Shown, key: string, savedAt: number, line?: string | null): Shown {
  const held = shown[key];
  if (!held) return shown;
  return { ...shown, [key]: { tick: line ? { ...held.tick, line } : held.tick, savedAt } };
}

/**
 * A mark that did not save: no longer shown (the job shows as the brief says it).
 *
 * @param shown - The marks shown.
 * @param key - The job's key.
 * @returns The marks shown now.
 */
export function shownDropped(shown: Shown, key: string): Shown {
  if (!(key in shown)) return shown;
  const next = { ...shown };
  delete next[key];
  return next;
}

/**
 * A brief arrived that was asked for at askedAt: it shows every mark saved by then, so those are let go of. A mark
 * still on its way, or saved after the brief was asked for, stays shown.
 *
 * @param shown - The marks shown.
 * @param askedAt - When the brief was asked for (ms).
 * @returns The marks shown now.
 */
export function shownAfterBrief(shown: Shown, askedAt: number): Shown {
  const next: Shown = {};
  let changed = false;
  for (const [key, held] of Object.entries(shown)) {
    if (held.savedAt !== null && held.savedAt <= askedAt) changed = true;
    else next[key] = held;
  }
  return changed ? next : shown;
}

/**
 * The ticks shown, by job key, as the cards read them.
 *
 * @param shown - The marks shown.
 * @returns The ticks by key.
 */
export function pendingOf(shown: Shown): Record<string, PendingTick> {
  const out: Record<string, PendingTick> = {};
  for (const [key, held] of Object.entries(shown)) out[key] = held.tick;
  return out;
}

/** A job as markLanded reads it from the brief. */
type LandedJob = Pick<TodoJob, 'key' | 'base' | 'done' | 'skipped' | 'moved' | 'missed' | 'calm' | 'group' | 'planting' | 'plants'>;

/**
 * Whether the plants' rows hold these plants done (or not done).
 *
 * @param job - The job.
 * @param ids - The plants.
 * @param done - Done or not.
 * @returns True when every row with one of them says so.
 */
function plantsAre(job: LandedJob, ids: number[], done: boolean): boolean {
  const rows = (job.plants?.beds ?? []).flatMap((bed) => bed.rows).filter((row) => row.ids.some((id) => ids.includes(id)));
  return rows.length > 0 && rows.every((row) => row.done === done);
}

/**
 * Whether a brief shows a mark made on a job: for a mark that got no answer, read the brief again and look. Unpark:
 * the job is out from under When you can, or it is a When you can job by nature (calm), which reads the same parked
 * or not, so it is taken as saved (the screen shows the same either way).
 *
 * @param mark - The mark.
 * @param key - The job's key when it was marked (undefined: back on every postponed job).
 * @param jobs - The brief's jobs (todo.jobs).
 * @returns True when the brief shows it.
 */
export function markLanded(mark: TodoMark, key: string | undefined, jobs: LandedJob[]): boolean {
  const base = key ? key.split('|')[0] : null;
  const job = (key ? jobs.find((j) => j.key === key) : undefined) ?? (base ? jobs.find((j) => j.base === base) : undefined);
  switch (mark.action) {
    case 'done':
      if (!job) return false;
      return mark.plantIds ? plantsAre(job, mark.plantIds, true) : job.done && !job.skipped;
    case 'undone':
      if (!job) return false;
      return mark.plantIds ? plantsAre(job, mark.plantIds, false) : !job.done;
    case 'skip':
      return Boolean(job?.skipped);
    case 'unskip':
      return Boolean(job) && !job?.skipped;
    case 'today':
      return Boolean(job) && !job?.missed;
    case 'tomorrow':
    case 'later':
      return !jobs.some((j) => j.key === key) || Boolean(job?.moved);
    case 'whenever':
      return job?.group === 'whenever';
    case 'unpark':
      return Boolean(job) && (job?.group !== 'whenever' || Boolean(job?.calm));
    case 'back':
      return key ? Boolean(job) && !job?.moved : !jobs.some((j) => j.moved);
    case 'day':
      return Boolean(mark.day) && job?.planting?.on === mark.day;
    case 'remove':
      return !jobs.some((j) => j.base === base);
    case 'unremove':
      return jobs.some((j) => j.base === base);
    default:
      return false;
  }
}

/** What a mark needs to be made: its request, the brief's read, where the brief goes, and the clock. */
export interface MarkSteps {
  /** POST /todo/marks. */
  send: () => Promise<TodoMarkAnswer>;
  /** GET /today/brief (may be slow, may fail). */
  refresh: () => Promise<TodayBrief>;
  /** Puts a brief read in place; askedAt is when it was asked for. */
  apply: (brief: TodayBrief, askedAt: number) => void;
  /** Whether a failure got no answer at all (own-todos isNoAnswer). */
  noAnswer: (error: unknown) => boolean;
  /** The time now (ms). */
  now: () => number;
  /** Waits so long (ms) before a failed read of the brief is tried again. */
  wait: (ms: number) => Promise<void>;
  /** How long to wait before that one more try (ms). */
  retryMs: number;
}

/**
 * Puts briefs in place in the order they were asked for, whatever order they land in: a brief asked for before the
 * last one put in place is refused (a tick's read landing after its Undo's would show the job ticked again). One per
 * page (useTodoMarks keeps it), shared by its marks.
 *
 * @param apply - Puts a brief in place.
 * @returns The gate: true when the brief was put in place, false when a newer one already was.
 */
export function briefApplier(apply: (brief: TodayBrief, askedAt: number) => void): (brief: TodayBrief, askedAt: number) => boolean {
  let newest = -Infinity;
  return (brief, askedAt) => {
    if (askedAt < newest) return false;
    newest = askedAt;
    apply(brief, askedAt);
    return true;
  };
}

/** How a mark turned out: saved (with what to say, and the brief read after it), or failed. */
export type MarkOutcome =
  | { result: 'saved'; said: TodoSaid | null; savedAt: number; refreshed: Promise<boolean> }
  | { result: 'failed'; error: unknown };

/**
 * Reads the brief once and puts it in place; never rejects.
 *
 * @param steps - The steps.
 * @returns The brief read, or null when the read failed.
 */
async function readOnce(steps: MarkSteps): Promise<TodayBrief | null> {
  const askedAt = steps.now();
  try {
    const brief = await steps.refresh();
    steps.apply(brief, askedAt);
    return brief;
  } catch {
    return null;
  }
}

/**
 * Reads the brief and puts it in place, and when that read fails, once more after a short wait (so a mark shown
 * while the server was slow is let go of when the brief comes); never rejects.
 *
 * @param steps - The steps.
 * @returns The brief read, or null when both reads failed.
 */
async function readBrief(steps: MarkSteps): Promise<TodayBrief | null> {
  const first = await readOnce(steps);
  if (first) return first;
  await steps.wait(steps.retryMs);
  return readOnce(steps);
}

/**
 * Makes a mark: sends it, and once it is saved reads the brief again on its own (refreshed resolves true when the
 * brief came and was put in place, false when its read failed; never rejects, and never makes the mark a failure). A
 * mark with no answer at all reads the brief and looks for it (markLanded): there, saved (with nothing to say: the
 * answer and its toast were lost); not there, or the read failed, failed.
 *
 * @param mark - The mark.
 * @param key - The job's key (undefined: back on every postponed job).
 * @param steps - Its request, the brief's read, where the brief goes, the clock.
 * @returns How it turned out.
 */
export async function runMark(mark: TodoMark, key: string | undefined, steps: MarkSteps): Promise<MarkOutcome> {
  try {
    const answer = await steps.send();
    const savedAt = steps.now();
    const refreshed = readBrief(steps).then((brief) => brief !== null);
    return { result: 'saved', said: answer.said ?? null, savedAt, refreshed };
  } catch (error) {
    if (!steps.noAnswer(error)) return { result: 'failed', error };
    const brief = await readBrief(steps);
    if (brief && markLanded(mark, key, brief.todo?.jobs ?? [])) {
      return { result: 'saved', said: null, savedAt: steps.now(), refreshed: Promise.resolve(true) };
    }
    return { result: 'failed', error };
  }
}
